import { DislikeFilled, LikeFilled } from '@ant-design/icons'
import { Button, Form, Input, Select, Table, Typography } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import VSpace from '../../../common/VSpace'
import ChatSessionModal from '../../Chats/ChatSessionModal'
import {
  getDefaultRatingFilters,
  Rating,
  RatingFilters,
  ThumbsFilter,
} from './ratingsModel'
import { useRatingsListQuery } from './ratingsQueries'
import RatingDetailModal from './RatingDetailModal'

type FormValues = {
  conversationId?: string
  tenantId?: string
  userId?: string
  thumbs: ThumbsFilter
  q?: string
}

const formValuesToFilters = (values: FormValues, page: number, pageSize: number): RatingFilters => ({
  conversationId: values.conversationId || undefined,
  tenantId: values.tenantId || undefined,
  userId: values.userId || undefined,
  thumbs: values.thumbs ?? 'any',
  q: values.q || undefined,
  page,
  pageSize,
})

const RatingsExplorer = () => {
  const [form] = Form.useForm<FormValues>()
  const [filters, setFilters] = useState<RatingFilters>(() => getDefaultRatingFilters())
  const [selectedRatingId, setSelectedRatingId] = useState<string | null>(null)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const { data, isLoading } = useRatingsListQuery(filters)

  const handleApply = () => {
    const values = form.getFieldsValue()
    setFilters(formValuesToFilters(values, 1, filters.pageSize))
  }

  const handleReset = () => {
    form.resetFields()
    setFilters(getDefaultRatingFilters())
  }

  const columns = [
    {
      title: 'Rating',
      dataIndex: 'thumbs_up',
      width: 80,
      render: (val: boolean) =>
        val ? (
          <LikeFilled style={{ color: '#52c41a', fontSize: 18 }} />
        ) : (
          <DislikeFilled style={{ color: '#ff4d4f', fontSize: 18 }} />
        ),
    },
    { title: 'Tenant', dataIndex: 'tenant_name', width: 180,
      render: (val: string | null) =>
        val ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    { title: 'User', dataIndex: 'user_name', width: 180 },
    {
      title: 'Chat',
      dataIndex: 'conversation_id',
      width: 320,
      render: (val: string) => (
        <Typography.Link
          onClick={(e) => {
            e.stopPropagation()
            setSelectedConversationId(val)
          }}
          style={{ fontFamily: 'monospace', fontSize: 12 }}
        >
          {val}
        </Typography.Link>
      ),
    },
    {
      title: 'Response',
      dataIndex: 'response_text',
      ellipsis: true,
      render: (val: string) => (
        <Typography.Text ellipsis style={{ maxWidth: 400 }}>
          {val}
        </Typography.Text>
      ),
    },
    {
      title: 'Rationale',
      dataIndex: 'rationale',
      ellipsis: true,
      render: (val: string | null) =>
        val ? (
          <Typography.Text ellipsis style={{ maxWidth: 240 }}>
            {val}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      width: 180,
      render: (val: string) => dayjs(val).format('MMM D, YYYY h:mm A'),
    },
  ]

  return (
    <VSpace>
      <Form
        form={form}
        initialValues={{ thumbs: 'any' }}
        onFinish={handleApply}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Form.Item name="q" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
            <Input placeholder="Search response / rationale" allowClear />
          </Form.Item>
          <Form.Item name="thumbs" style={{ marginBottom: 0 }}>
            <Select
              style={{ width: 140 }}
              options={[
                { value: 'any', label: 'Any rating' },
                { value: 'up', label: 'Thumbs up' },
                { value: 'down', label: 'Thumbs down' },
              ]}
            />
          </Form.Item>
          <Form.Item name="userId" style={{ marginBottom: 0 }}>
            <Input placeholder="User ID" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="tenantId" style={{ marginBottom: 0 }}>
            <Input placeholder="Tenant ID" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="conversationId" style={{ marginBottom: 0 }}>
            <Input placeholder="Conversation ID" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">Apply</Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button onClick={handleReset}>Reset</Button>
          </Form.Item>
        </div>
      </Form>
      <Table<Rating>
        dataSource={data?.items}
        loading={isLoading}
        rowKey="id"
        columns={columns}
        onRow={(record) => ({
          onClick: () => setSelectedRatingId(record.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: filters.page,
          pageSize: filters.pageSize,
          total: data?.total,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
          showTotal: (total) => `${total} ratings`,
          onChange: (page, pageSize) =>
            setFilters((prev) => ({ ...prev, page, pageSize })),
        }}
      />
      <RatingDetailModal
        ratingId={selectedRatingId}
        onClose={() => setSelectedRatingId(null)}
        onOpenChat={(conversationId) => setSelectedConversationId(conversationId)}
      />
      <ChatSessionModal
        sessionId={selectedConversationId}
        onClose={() => setSelectedConversationId(null)}
      />
    </VSpace>
  )
}

export default RatingsExplorer
